import React, { useRef, useState, useMemo, useEffect } from "react";
import { ScanFields as ScanFieldsType } from "../types";
import { Line } from "../types";
import { useSubinventoryLov } from "../hooks/useSubinventoryLov";
import { usePalletDetails } from "../hooks/usePalletDetails";
import { useCageDetails } from "../hooks/useCageDetails";
import { useToteDetails } from "../hooks/useToteDetails";

interface ScanFieldsProps {
  scanFields: ScanFieldsType;
  onScanFieldsChange: (fields: ScanFieldsType) => void;
  onSerialNumberScan: (serialNumber: string) => void;
  isDarkTheme?: boolean;
  selectedHeaderToOrg?: string;
  lines?: Line[];
  clearSerialNotFoundRef?: React.MutableRefObject<(() => void) | null>;
  serialScanLoading?: boolean;
}

const ScanFields: React.FC<ScanFieldsProps> = ({
  scanFields,
  onScanFieldsChange,
  onSerialNumberScan,
  isDarkTheme,
  selectedHeaderToOrg,
  lines = [],
  clearSerialNotFoundRef,
  serialScanLoading = false,
}) => {
  const { data: dynamicSubinventories, refetch: refetchSubinventories } = useSubinventoryLov(selectedHeaderToOrg, false);
  const subinventories = dynamicSubinventories;
  const { data: pallets, refetch: refetchPalletDetails, isLoading: palletLoading } = usePalletDetails(selectedHeaderToOrg, undefined, false);
  const { data: cages, refetch: refetchCageDetails } = useCageDetails(selectedHeaderToOrg, scanFields.pallet, true);
  const { data: totes, refetch: refetchToteDetails } = useToteDetails(selectedHeaderToOrg, scanFields.pallet, scanFields.cage, true);

  const palletInputRef = useRef<HTMLInputElement>(null);
  const trackingInputRef = useRef<HTMLInputElement>(null);
  const serialInputRef = useRef<HTMLInputElement>(null);
  const cageInputRef = useRef<HTMLInputElement>(null);
  const toteInputRef = useRef<HTMLInputElement>(null);
  const palletDropdownRef = useRef<HTMLDivElement>(null);
  const subinventoryDropdownRef = useRef<HTMLDivElement>(null);
  const cageDropdownRef = useRef<HTMLDivElement>(null);
  const toteDropdownRef = useRef<HTMLDivElement>(null);
  const [showPalletDropdown, setShowPalletDropdown] = useState(false);
  const [showSubinventoryDropdown, setShowSubinventoryDropdown] = useState(false);
  const [showCageDropdown, setShowCageDropdown] = useState(false);
  const [showToteDropdown, setShowToteDropdown] = useState(false);
  const [subinventorySearch, setSubinventorySearch] = useState('');
  const [cageSearch, setCageSearch] = useState('');
  const [toteSearch, setToteSearch] = useState('');
  const [selectedSubinventoryIndex, setSelectedSubinventoryIndex] = useState(-1);
  const [selectedCageIndex, setSelectedCageIndex] = useState(-1);
  const [selectedToteIndex, setSelectedToteIndex] = useState(-1);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [serialNotFoundMessage, setSerialNotFoundMessage] = useState('');
  const [dropdownJustOpened, setDropdownJustOpened] = useState(false);
  
  // Expose clear function to parent via ref
  useEffect(() => {
    if (clearSerialNotFoundRef) {
      clearSerialNotFoundRef.current = () => setSerialNotFoundMessage('');
    }
  }, [clearSerialNotFoundRef]);
  
  // Auto-focus pallet field and show dropdown when pallets are loaded
  useEffect(() => {
    console.log('Pallet focus check:', { palletsLength: pallets.length, palletLoading, selectedHeaderToOrg });
    if (pallets.length > 0 && !palletLoading && selectedHeaderToOrg) {
      console.log('Focusing pallet field');
      setTimeout(() => {
        if (palletInputRef.current) {
          palletInputRef.current.focus();
          setShowPalletDropdown(true);
          console.log('Pallet field focused and dropdown shown');
        }
      }, 200);
    }
  }, [pallets, palletLoading, selectedHeaderToOrg]);

  // Trigger pallet API call when header is selected
  useEffect(() => {
    console.log('ScanFields received selectedHeaderToOrg:', selectedHeaderToOrg);
    if (selectedHeaderToOrg) {
      console.log('Calling pallet API for:', selectedHeaderToOrg);
      refetchPalletDetails(selectedHeaderToOrg);
    }
  }, [selectedHeaderToOrg, refetchPalletDetails]);

  // Click outside handler for pallet dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        palletDropdownRef.current &&
        !palletDropdownRef.current.contains(event.target as Node)
      ) {
        setShowPalletDropdown(false);
      }
    };

    if (showPalletDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showPalletDropdown]);
  


  const filteredSubinventories = useMemo(() => {
    if (!subinventorySearch) return subinventories;
    return subinventories.filter((subinv) =>
      subinv.toLowerCase().includes(subinventorySearch.toLowerCase())
    );
  }, [subinventories, subinventorySearch]);

  const filteredCages = useMemo(() => {
    if (!cageSearch) return cages.map(c => c.cage);
    return cages.filter((c) =>
      c.cage.toLowerCase().includes(cageSearch.toLowerCase())
    ).map(c => c.cage);
  }, [cages, cageSearch]);

  const filteredTotes = useMemo(() => {
    if (!toteSearch) return totes.map(t => t.tote);
    return totes.filter((t) =>
      t.tote.toLowerCase().includes(toteSearch.toLowerCase())
    ).map(t => t.tote);
  }, [totes, toteSearch]);



  const handleSubinventoryKeyDown = (e: React.KeyboardEvent) => {
    console.log('KeyDown event:', e.key, 'selectedIndex:', selectedSubinventoryIndex);
    if (!showSubinventoryDropdown || filteredSubinventories.length === 0) return;
    
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      // Ignore first ArrowDown if dropdown just opened (scanner behavior)
      if (dropdownJustOpened) {
        console.log('Ignoring ArrowDown - dropdown just opened');
        setDropdownJustOpened(false);
        return;
      }
      setSelectedSubinventoryIndex(prev => {
        const newIndex = prev < filteredSubinventories.length - 1 ? prev + 1 : 0;
        console.log('ArrowDown - new index:', newIndex);
        // Scroll selected item into view
        setTimeout(() => {
          const dropdown = subinventoryDropdownRef.current;
          const selectedItem = dropdown?.children[newIndex] as HTMLElement;
          selectedItem?.scrollIntoView({ block: 'nearest' });
        }, 0);
        return newIndex;
      });
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedSubinventoryIndex(prev => {
        const newIndex = prev > 0 ? prev - 1 : filteredSubinventories.length - 1;
        console.log('ArrowUp - new index:', newIndex);
        // Scroll selected item into view
        setTimeout(() => {
          const dropdown = subinventoryDropdownRef.current;
          const selectedItem = dropdown?.children[newIndex] as HTMLElement;
          selectedItem?.scrollIntoView({ block: 'nearest' });
        }, 0);
        return newIndex;
      });
    } else if (e.key === 'Enter') {
      console.log('Enter pressed - selectedIndex:', selectedSubinventoryIndex);
      if (selectedSubinventoryIndex >= 0) {
        e.preventDefault();
        const selectedSubinventory = filteredSubinventories[selectedSubinventoryIndex];
        console.log('Selecting subinventory:', selectedSubinventory);
        onScanFieldsChange({
          ...scanFields,
          toSubinventory: selectedSubinventory,
          toLocator: "",
          scanSerialNumber: fieldErrors.toSubinventory ? "" : scanFields.scanSerialNumber,
        });
        if (fieldErrors.toSubinventory) {
          setFieldErrors(prev => ({ ...prev, toSubinventory: '' }));
        }

        setShowSubinventoryDropdown(false);
        setSubinventorySearch('');
        setSelectedSubinventoryIndex(-1);
      } else {
        console.log('Enter pressed but no item selected - ignoring');
      }
    } else if (e.key === 'Escape') {
      setShowSubinventoryDropdown(false);
      setSubinventorySearch('');
      setSelectedSubinventoryIndex(-1);
    }
  };

  const validateMandatoryFields = (serialNumber: string): boolean => {
    const errors: Record<string, string> = {};
    
    if (!scanFields.toSubinventory || scanFields.toSubinventory.trim() === '') {
      errors.toSubinventory = 'To Subinventory is mandatory and cannot be null';
    }
    
    if (!scanFields.trackingNumber || scanFields.trackingNumber.trim() === '') {
      errors.trackingNumber = 'Tracking Number is mandatory and cannot be null';
    }
    
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSerialNumberChange = (value: string) => {
    console.log('Serial number changed to:', value);
    const updatedFields = { ...scanFields, scanSerialNumber: value };
    onScanFieldsChange(updatedFields);
    if (serialNotFoundMessage) {
      setSerialNotFoundMessage('');
    }
    
    // Auto-trigger validation when serial number is pasted/scanned
    if (value.trim() && value.length > 5) { // Assuming serial numbers are longer than 5 chars
      setTimeout(() => {
        console.log('Auto-triggering validation for:', value);
        if (validateMandatoryFields(value.trim())) {
          console.log('Auto-validation passed, calling onSerialNumberScan');
          onSerialNumberScan(value.trim());
        } else {
          console.log('Auto-validation failed');
        }
      }, 100); // Small delay to ensure state is updated
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && scanFields.scanSerialNumber.trim()) {
      console.log('Enter pressed with serial number:', scanFields.scanSerialNumber.trim());
      if (validateMandatoryFields(scanFields.scanSerialNumber.trim())) {
        console.log('Validation passed, calling onSerialNumberScan');
        onSerialNumberScan(scanFields.scanSerialNumber.trim());
      } else {
        console.log('Validation failed');
      }
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        subinventoryDropdownRef.current &&
        !subinventoryDropdownRef.current.contains(event.target as Node)
      ) {
        setShowSubinventoryDropdown(false);
        setSubinventorySearch('');
      }
    };

    if (showSubinventoryDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showSubinventoryDropdown]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        cageDropdownRef.current &&
        !cageDropdownRef.current.contains(event.target as Node)
      ) {
        setShowCageDropdown(false);
        setCageSearch('');
      }
    };

    if (showCageDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showCageDropdown]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        toteDropdownRef.current &&
        !toteDropdownRef.current.contains(event.target as Node)
      ) {
        setShowToteDropdown(false);
        setToteSearch('');
      }
    };

    if (showToteDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showToteDropdown]);

  return (
    <div
      style={{
        marginBottom: "10px",
        padding: "15px",
        border: `1px solid ${isDarkTheme ? "#555" : "#ccc"}`,
        borderRadius: "4px",
        backgroundColor: isDarkTheme ? "#2a2a2a" : "#fff",
        width: "fit-content",
      }}
    >
      <div style={{ display: "flex", gap: "15px", alignItems: "flex-start" }}>
        <div style={{ position: "relative", display: "flex", flexDirection: "column" }}>
          <input
            ref={palletInputRef}
            type="text"
            value={scanFields.pallet}
            onChange={(e) => onScanFieldsChange({ ...scanFields, pallet: e.target.value })}
            onFocus={() => setShowPalletDropdown(true)}
            onClick={() => setShowPalletDropdown(true)}
            placeholder="Enter pallet"
            style={{
              padding: "8px",
              border: `2px solid ${isDarkTheme ? "#555" : "#ccc"}`,
              borderRadius: "4px",
              width: "260px",
              backgroundColor: isDarkTheme ? "#333" : "#fff",
              color: isDarkTheme ? "#fff" : "#000",
              boxSizing: "border-box",
            }}
          />
          {showPalletDropdown && pallets.length > 0 && (
            <div
              ref={palletDropdownRef}
              style={{
                position: "absolute",
                top: "100%",
                left: "0",
                backgroundColor: isDarkTheme ? "#333" : "#fff",
                border: `1px solid ${isDarkTheme ? "#555" : "#ccc"}`,
                borderRadius: "4px",
                zIndex: 1000,
                width: "262px",
                maxHeight: "200px",
                overflowY: "auto",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              }}
            >
              {pallets.map((palletData, index) => (
                <div
                  key={`${palletData.pallet}-${index}`}
                  onClick={() => {
                    onScanFieldsChange({ ...scanFields, pallet: palletData.pallet, toSubinventory: "", toLocator: "" });
                    setShowPalletDropdown(false);
                    
                    // Auto-populate subinventory after pallet selection
                    if (selectedHeaderToOrg && palletData.subinventoryCode) {
                      setTimeout(() => {
                        refetchSubinventories(selectedHeaderToOrg, palletData.subinventoryCode, (subinventoryData) => {
                          if (subinventoryData.length > 0) {
                            onScanFieldsChange({
                              ...scanFields,
                              pallet: palletData.pallet,
                              toSubinventory: subinventoryData[0],
                              toLocator: ""
                            });
                          }
                        });
                      }, 100);
                    }
                  }}
                  style={{
                    padding: "8px 12px",
                    cursor: "pointer",
                    backgroundColor: "transparent",
                    color: isDarkTheme ? "#fff" : "#000",
                    borderBottom: `1px solid ${isDarkTheme ? "#555" : "#eee"}`,
                    fontSize: "12px",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = isDarkTheme ? "#444" : "#f5f5f5";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                  }}
                >
                  {palletData.pallet}
                </div>
              ))}
            </div>
          )}
        </div>
        <div style={{ position: "relative", display: "flex", flexDirection: "column" }}>
          <div style={{ position: "relative" }}>
            <input
              type="text"
              value={subinventorySearch || scanFields.toSubinventory}
              onChange={(e) => {
                const value = e.target.value;
                console.log('onChange triggered with value:', value);
                setSubinventorySearch(value);
                
                // Update parent state only when user selects from dropdown
                onScanFieldsChange({
                  ...scanFields,
                  toSubinventory: value,
                  toLocator: "",
                  scanSerialNumber: fieldErrors.toSubinventory ? "" : scanFields.scanSerialNumber,
                });
                if (fieldErrors.toSubinventory) {
                  setFieldErrors(prev => ({ ...prev, toSubinventory: '' }));
                }
                
                // Show dropdown and trigger API only if needed
                if (value.trim()) {
                  console.log('Showing dropdown for value:', value);
                  setShowSubinventoryDropdown(true);
                  setDropdownJustOpened(true);
                  setTimeout(() => setDropdownJustOpened(false), 200);
                  // Don't call API here since it's called from pallet selection
                } else {
                  setShowSubinventoryDropdown(false);
                }
              }}
              onFocus={() => {
                setSubinventorySearch(scanFields.toSubinventory);
                if (scanFields.toSubinventory) {
                  setShowSubinventoryDropdown(true);
                }
                setSelectedSubinventoryIndex(-1);
              }}
              onKeyDown={handleSubinventoryKeyDown}
              onClick={() => {
                setSubinventorySearch(scanFields.toSubinventory);
                setShowSubinventoryDropdown(true);
                setSelectedSubinventoryIndex(-1);
              }}
              placeholder="Search subinventory..."
              style={{
                padding: "8px 30px 8px 8px",
                border: `1px solid ${isDarkTheme ? "#555" : "#ccc"}`,
                borderRadius: "4px",
                width: "150px",
                backgroundColor: isDarkTheme ? "#333" : "#fff",
                color: isDarkTheme ? "#fff" : "#000",
                boxSizing: "border-box",
              }}
            />
            {scanFields.toSubinventory ? (
              <span
                onClick={() => {
                  onScanFieldsChange({ ...scanFields, toSubinventory: "", toLocator: "", scanSerialNumber: "" });
                  setShowSubinventoryDropdown(false);
                  if (fieldErrors.toSubinventory) {
                    setFieldErrors(prev => ({ ...prev, toSubinventory: '' }));
                  }
                }}
                style={{
                  position: "absolute",
                  right: "8px",
                  top: "8px",
                  cursor: "pointer",
                  color: isDarkTheme ? "#ccc" : "#666",
                  fontSize: "20px",
                  fontWeight: "bold",
                  lineHeight: "1",
                  height: "20px",
                }}
              >
                ×
              </span>
            ) : (
              <span
                style={{
                  position: "absolute",
                  right: "8px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  pointerEvents: "none",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8"></circle>
                  <path d="m21 21-4.35-4.35"></path>
                </svg>
              </span>
            )}
          </div>
          {showSubinventoryDropdown && filteredSubinventories.length > 0 && (
            <div
              ref={subinventoryDropdownRef}
              style={{
                position: "absolute",
                top: "100%",
                left: "0",
                backgroundColor: isDarkTheme ? "#333" : "#fff",
                border: `1px solid ${isDarkTheme ? "#555" : "#ccc"}`,
                borderRadius: "4px",
                zIndex: 1000,
                width: "150px",
                maxHeight: "200px",
                overflowY: "auto",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              }}
            >
              {filteredSubinventories.map((subinventory, index) => (
                <div
                  key={subinventory}
                  onClick={() => {
                    onScanFieldsChange({
                      ...scanFields,
                      toSubinventory: subinventory,
                      toLocator: "",
                      scanSerialNumber: fieldErrors.toSubinventory ? "" : scanFields.scanSerialNumber,
                    });
                    if (fieldErrors.toSubinventory) {
                      setFieldErrors(prev => ({ ...prev, toSubinventory: '' }));
                    }
                    setShowSubinventoryDropdown(false);
                    setSubinventorySearch('');
                    setSelectedSubinventoryIndex(-1);
                  }}
                  style={{
                    padding: "8px 12px",
                    cursor: "pointer",
                    backgroundColor: selectedSubinventoryIndex === index ? (isDarkTheme ? "#555" : "#e3f2fd") : "transparent",
                    color: isDarkTheme ? "#fff" : "#000",
                    borderBottom: `1px solid ${isDarkTheme ? "#555" : "#eee"}`,
                    fontSize: "12px",
                  }}
                  onMouseEnter={(e) => {
                    if (selectedSubinventoryIndex !== index) {
                      e.currentTarget.style.backgroundColor = isDarkTheme ? "#444" : "#f5f5f5";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedSubinventoryIndex !== index) {
                      e.currentTarget.style.backgroundColor = "transparent";
                    }
                  }}
                >
                  {subinventory}
                </div>
              ))}
            </div>
          )}
          {fieldErrors.toSubinventory && (
            <div style={{
              color: '#dc3545',
              fontSize: '11px',
              marginTop: '2px',
              minHeight: '14px'
            }}>
              {fieldErrors.toSubinventory}
            </div>
          )}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "8px", position: "relative" }}>
          <div style={{ position: "relative" }}>
            <input
              ref={cageInputRef}
              type="text"
              value={showCageDropdown ? cageSearch : scanFields.cage}
              onChange={(e) => {
                const value = e.target.value;
                
                if (showCageDropdown) {
                  setCageSearch(value);
                } else {
                  if (value.trim()) {
                    setCageSearch(value);
                    setShowCageDropdown(true);
                    if (selectedHeaderToOrg && scanFields.pallet) {
                      refetchCageDetails(selectedHeaderToOrg, scanFields.pallet, value);
                    }
                  } else {
                    setShowCageDropdown(false);
                  }
                }
              }}
              onFocus={() => {
                setCageSearch(scanFields.cage);
                setShowCageDropdown(true);
                setSelectedCageIndex(-1);
                if (selectedHeaderToOrg && scanFields.pallet) {
                  refetchCageDetails(selectedHeaderToOrg, scanFields.pallet);
                }
              }}
              onClick={() => {
                setCageSearch(scanFields.cage);
                setShowCageDropdown(true);
                setSelectedCageIndex(-1);
              }}
              placeholder="Enter cage"
              style={{
                padding: "8px 30px 8px 8px",
                border: `2px solid ${isDarkTheme ? "#555" : "#ccc"}`,
                borderRadius: "4px",
                width: "120px",
                backgroundColor: isDarkTheme ? "#333" : "#fff",
                color: isDarkTheme ? "#fff" : "#000",
                boxSizing: "border-box",
              }}
            />
            {scanFields.cage ? (
              <span
                onClick={() => {
                  onScanFieldsChange({ ...scanFields, cage: "" });
                  setShowCageDropdown(false);
                }}
                style={{
                  position: "absolute",
                  right: "8px",
                  top: "8px",
                  cursor: "pointer",
                  color: isDarkTheme ? "#ccc" : "#666",
                  fontSize: "20px",
                  fontWeight: "bold",
                  lineHeight: "1",
                  height: "20px",
                }}
              >
                ×
              </span>
            ) : (
              <span
                style={{
                  position: "absolute",
                  right: "8px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  pointerEvents: "none",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8"></circle>
                  <path d="m21 21-4.35-4.35"></path>
                </svg>
              </span>
            )}
            {showCageDropdown && filteredCages.length > 0 && (
              <div
                ref={cageDropdownRef}
                style={{
                  position: "absolute",
                  top: "100%",
                  left: "0",
                  backgroundColor: isDarkTheme ? "#333" : "#fff",
                  border: `1px solid ${isDarkTheme ? "#555" : "#ccc"}`,
                  borderRadius: "4px",
                  zIndex: 1000,
                  width: "120px",
                  maxHeight: "200px",
                  overflowY: "auto",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                }}
              >
                {filteredCages.map((cage, index) => (
                  <div
                    key={cage}
                    onClick={() => {
                      onScanFieldsChange({ ...scanFields, cage });
                      setShowCageDropdown(false);
                      setCageSearch('');
                      setSelectedCageIndex(-1);
                      setTimeout(() => toteInputRef.current?.focus(), 100);
                    }}
                    style={{
                      padding: "8px 12px",
                      cursor: "pointer",
                      backgroundColor: selectedCageIndex === index ? (isDarkTheme ? "#555" : "#e3f2fd") : "transparent",
                      color: isDarkTheme ? "#fff" : "#000",
                      borderBottom: `1px solid ${isDarkTheme ? "#555" : "#eee"}`,
                      fontSize: "12px",
                    }}
                    onMouseEnter={(e) => {
                      if (selectedCageIndex !== index) {
                        e.currentTarget.style.backgroundColor = isDarkTheme ? "#444" : "#f5f5f5";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedCageIndex !== index) {
                        e.currentTarget.style.backgroundColor = "transparent";
                      }
                    }}
                  >
                    {cage}
                  </div>
                ))}
              </div>
            )}
          </div>
          <span
            onClick={() => {
              onScanFieldsChange({ ...scanFields, cage: "0" });
              setShowCageDropdown(false);
              setCageSearch('');
              setSelectedCageIndex(-1);
              setTimeout(() => toteInputRef.current?.focus(), 100);
            }}
            style={{
              fontSize: "14px",
              color: isDarkTheme ? "#007bff" : "#0066cc",
              cursor: "pointer",
              textDecoration: "underline",
              fontWeight: "500",
            }}
          >
            Skip
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "8px", position: "relative" }}>
          <div style={{ position: "relative" }}>
            <input
              ref={toteInputRef}
              type="text"
              value={showToteDropdown ? toteSearch : scanFields.tote}
              onChange={(e) => {
                const value = e.target.value;
                
                if (showToteDropdown) {
                  setToteSearch(value);
                } else {
                  if (value.trim()) {
                    setToteSearch(value);
                    setShowToteDropdown(true);
                    if (selectedHeaderToOrg && scanFields.pallet && scanFields.cage) {
                      refetchToteDetails(selectedHeaderToOrg, scanFields.pallet, scanFields.cage, value);
                    }
                  } else {
                    setShowToteDropdown(false);
                  }
                }
              }}
              onFocus={() => {
                setToteSearch(scanFields.tote);
                setShowToteDropdown(true);
                setSelectedToteIndex(-1);
                if (selectedHeaderToOrg && scanFields.pallet && scanFields.cage) {
                  refetchToteDetails(selectedHeaderToOrg, scanFields.pallet, scanFields.cage);
                }
              }}
              onClick={() => {
                setToteSearch(scanFields.tote);
                setShowToteDropdown(true);
                setSelectedToteIndex(-1);
              }}
              placeholder="Enter tote"
              style={{
                padding: "8px 30px 8px 8px",
                border: `2px solid ${isDarkTheme ? "#555" : "#ccc"}`,
                borderRadius: "4px",
                width: "120px",
                backgroundColor: isDarkTheme ? "#333" : "#fff",
                color: isDarkTheme ? "#fff" : "#000",
                boxSizing: "border-box",
              }}
            />
            {scanFields.tote ? (
              <span
                onClick={() => {
                  onScanFieldsChange({ ...scanFields, tote: "" });
                  setShowToteDropdown(false);
                }}
                style={{
                  position: "absolute",
                  right: "8px",
                  top: "8px",
                  cursor: "pointer",
                  color: isDarkTheme ? "#ccc" : "#666",
                  fontSize: "20px",
                  fontWeight: "bold",
                  lineHeight: "1",
                  height: "20px",
                }}
              >
                ×
              </span>
            ) : (
              <span
                style={{
                  position: "absolute",
                  right: "8px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  pointerEvents: "none",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8"></circle>
                  <path d="m21 21-4.35-4.35"></path>
                </svg>
              </span>
            )}
            {showToteDropdown && filteredTotes.length > 0 && (
              <div
                ref={toteDropdownRef}
                style={{
                  position: "absolute",
                  top: "100%",
                  left: "0",
                  backgroundColor: isDarkTheme ? "#333" : "#fff",
                  border: `1px solid ${isDarkTheme ? "#555" : "#ccc"}`,
                  borderRadius: "4px",
                  zIndex: 1000,
                  width: "120px",
                  maxHeight: "200px",
                  overflowY: "auto",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                }}
              >
                {filteredTotes.map((tote, index) => (
                  <div
                    key={tote}
                    onClick={() => {
                      onScanFieldsChange({ ...scanFields, tote });
                      setShowToteDropdown(false);
                      setToteSearch('');
                      setSelectedToteIndex(-1);
                      setTimeout(() => trackingInputRef.current?.focus(), 100);
                    }}
                    style={{
                      padding: "8px 12px",
                      cursor: "pointer",
                      backgroundColor: selectedToteIndex === index ? (isDarkTheme ? "#555" : "#e3f2fd") : "transparent",
                      color: isDarkTheme ? "#fff" : "#000",
                      borderBottom: `1px solid ${isDarkTheme ? "#555" : "#eee"}`,
                      fontSize: "12px",
                    }}
                    onMouseEnter={(e) => {
                      if (selectedToteIndex !== index) {
                        e.currentTarget.style.backgroundColor = isDarkTheme ? "#444" : "#f5f5f5";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedToteIndex !== index) {
                        e.currentTarget.style.backgroundColor = "transparent";
                      }
                    }}
                  >
                    {tote}
                  </div>
                ))}
              </div>
            )}
          </div>
          <span
            onClick={() => {
              onScanFieldsChange({ ...scanFields, tote: "0" });
              setShowToteDropdown(false);
              setToteSearch('');
              setSelectedToteIndex(-1);
              setTimeout(() => trackingInputRef.current?.focus(), 100);
            }}
            style={{
              fontSize: "14px",
              color: isDarkTheme ? "#007bff" : "#0066cc",
              cursor: "pointer",
              textDecoration: "underline",
              fontWeight: "500",
            }}
          >
            Skip
          </span>
        </div>

        <div style={{ display: "flex", flexDirection: "column" }}>
          <div style={{ position: "relative" }}>
            <input
              ref={trackingInputRef}
              type="text"
              value={scanFields.trackingNumber}
              onChange={(e) => {
                onScanFieldsChange({ ...scanFields, trackingNumber: e.target.value });
                if (fieldErrors.trackingNumber) {
                  setFieldErrors(prev => ({ ...prev, trackingNumber: '' }));
                }
              }}
              placeholder="Enter tracking number *"
              style={{
                padding: "8px",
                paddingRight: scanFields.trackingNumber ? "30px" : "8px",
                border: `2px solid ${fieldErrors.trackingNumber ? '#dc3545' : (isDarkTheme ? "#555" : "#ccc")}`,
                borderRadius: "4px",
                width: "200px",
                backgroundColor: isDarkTheme ? "#333" : "#fff",
                color: isDarkTheme ? "#fff" : "#000",
                boxSizing: "border-box",
              }}
            />
            {scanFields.trackingNumber && (
              <span
                onClick={() => {
                  onScanFieldsChange({ ...scanFields, trackingNumber: "" });
                  if (fieldErrors.trackingNumber) {
                    setFieldErrors(prev => ({ ...prev, trackingNumber: '' }));
                  }
                }}
                style={{
                  position: "absolute",
                  right: "8px",
                  top: "8px",
                  cursor: "pointer",
                  color: isDarkTheme ? "#ccc" : "#666",
                  fontSize: "20px",
                  fontWeight: "bold",
                  lineHeight: "1",
                  height: "20px",
                }}
              >
                ×
              </span>
            )}
          </div>
          {fieldErrors.trackingNumber && (
            <div style={{
              color: '#dc3545',
              fontSize: '11px',
              marginTop: '2px',
              minHeight: '14px'
            }}>
              {fieldErrors.trackingNumber}
            </div>
          )}
        </div>
        <div style={{ position: "relative", display: "flex", flexDirection: "column" }}>
          <div style={{ position: "relative" }}>
            <input
              ref={serialInputRef}
              type="text"
              value={scanFields.scanSerialNumber}
              onChange={(e) => handleSerialNumberChange(e.target.value)}
              onKeyPress={handleKeyPress}
              onFocus={() => {}}
              placeholder="Scan or enter serial number"
              style={{
                padding: "8px",
                paddingRight: scanFields.scanSerialNumber ? "30px" : "8px",
                border: `2px solid ${isDarkTheme ? "#555" : "#ccc"}`,
                borderRadius: "4px",
                width: "200px",
                backgroundColor: isDarkTheme ? "#333" : "#fff",
                color: isDarkTheme ? "#fff" : "#000",
                boxSizing: "border-box",
              }}
            />
            {scanFields.scanSerialNumber && !serialScanLoading && (
              <span
                onClick={() => {
                  const updatedFields = { ...scanFields, scanSerialNumber: "" };
                  onScanFieldsChange(updatedFields);
                  setSerialNotFoundMessage('');
                }}
                style={{
                  position: "absolute",
                  right: "8px",
                  top: "8px",
                  cursor: "pointer",
                  color: isDarkTheme ? "#ccc" : "#666",
                  fontSize: "20px",
                  fontWeight: "bold",
                  lineHeight: "1",
                  height: "20px",
                }}
              >
                ×
              </span>
            )}
            {serialScanLoading && (
              <>
                <style>
                  {`
                    @keyframes spin {
                      0% { transform: rotate(0deg); }
                      100% { transform: rotate(360deg); }
                    }
                  `}
                </style>
                <div
                  style={{
                    position: "absolute",
                    right: "8px",
                    top: "8px",
                    width: "20px",
                    height: "20px",
                    border: "2px solid #f3f3f3",
                    borderTop: "2px solid #007bff",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite"
                  }}
                />
              </>
            )}
          </div>
          {serialNotFoundMessage && (
            <div style={{
              color: '#dc3545',
              fontSize: '11px',
              marginTop: '2px',
              minHeight: '14px'
            }}>
              {serialNotFoundMessage}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScanFields;